import React, { useEffect, useMemo, useState } from 'react';

// Modale "Importa da altra società", condivisa da SMTP Email, SMTP PEC e
// Integrazione AI. Ricalca quella dei template di stampa (ReportPage) e ne usa
// le classi bop-import-report__*: le chiamate API restano nelle pagine, che le
// passano come funzioni, così ogni pagina documenta i propri endpoint (@api).

export interface SocietaSorgente { id: number; nome: string; conteggio: number }

export interface EsitoImport {
  id: number;
  nome: string;
  stato: 'IMPORTATO' | 'SALTATO';
  motivo?: string | null;
  nota?: string | null;
}

export interface ColonnaImport<T> { titolo: string; valore: (el: T) => React.ReactNode }

interface Props<T extends { id: number }> {
  titolo: string;
  destinazioneId: string;
  nomeDestinazione: string;
  intro: React.ReactNode;
  testoRicerca: (el: T) => string;
  colonne: ColonnaImport<T>[];
  caricaSocieta: () => Promise<SocietaSorgente[]>;
  caricaElementi: (sorgenteId: number) => Promise<T[]>;
  importa: (ids: number[]) => Promise<EsitoImport[]>;
  notaEsito?: React.ReactNode;
  onChiudi: () => void;
  onImportato: () => void;
}

function messaggio(e: unknown, fallback: string) {
  return e instanceof Error ? e.message : fallback;
}

export default function ImportaDaSocietaModal<T extends { id: number }>({
  titolo, destinazioneId, nomeDestinazione, intro, testoRicerca, colonne,
  caricaSocieta, caricaElementi, importa, notaEsito, onChiudi, onImportato,
}: Props<T>) {
  const [sorgenti, setSorgenti] = useState<SocietaSorgente[]>([]);
  const [sorgenteId, setSorgenteId] = useState('');
  const [elementi, setElementi] = useState<T[]>([]);
  const [caricando, setCaricando] = useState(false);
  const [selezionati, setSelezionati] = useState<number[]>([]);
  const [filtro, setFiltro] = useState('');
  const [importando, setImportando] = useState(false);
  const [errore, setErrore] = useState('');
  const [esiti, setEsiti] = useState<EsitoImport[] | null>(null);

  useEffect(() => {
    caricaSocieta()
      .then(setSorgenti)
      .catch(e => setErrore(messaggio(e, 'Errore nel caricamento delle società')));
  }, [caricaSocieta]);

  // La destinazione non può essere anche l'origine.
  const disponibili = sorgenti.filter(s => String(s.id) !== String(destinazioneId));

  const cambiaSorgente = async (id: string) => {
    setSorgenteId(id);
    setSelezionati([]);
    setElementi([]);
    setErrore('');
    if (!id) return;
    setCaricando(true);
    try {
      setElementi(await caricaElementi(Number(id)));
    } catch (e) {
      setErrore(messaggio(e, 'Errore nel caricamento delle configurazioni'));
    } finally {
      setCaricando(false);
    }
  };

  const filtrati = useMemo(() => elementi.filter(el =>
    !filtro || testoRicerca(el).toLowerCase().includes(filtro.toLowerCase())
  ), [elementi, filtro, testoRicerca]);

  const tuttiSelezionati = filtrati.length > 0 && filtrati.every(el => selezionati.includes(el.id));

  const toggle = (id: number) =>
    setSelezionati(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const toggleTutti = () => {
    const visibili = filtrati.map(el => el.id);
    setSelezionati(prev => (tuttiSelezionati
      ? prev.filter(id => !visibili.includes(id))
      : prev.concat(visibili.filter(id => !prev.includes(id)))));
  };

  const esegui = async () => {
    if (selezionati.length === 0) return;
    setImportando(true);
    setErrore('');
    try {
      setEsiti(await importa(selezionati));
      setSelezionati([]);
      onImportato();
    } catch (e) {
      setErrore(messaggio(e, 'Errore nell\'importazione'));
    } finally {
      setImportando(false);
    }
  };

  const importati = esiti ? esiti.filter(e => e.stato === 'IMPORTATO').length : 0;

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--xl">
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">{titolo}</h2>
          <button className="bop-btn-icon" onClick={onChiudi} aria-label="Chiudi">✕</button>
        </div>

        <div className="bop-modal__body">
          {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

          {esiti ? (
            <>
              <div className={`bop-alert ${importati > 0 ? 'bop-alert--success' : 'bop-alert--warning'}`}>
                {importati === 0
                  ? 'Nessuna configurazione importata in '
                  : importati === 1 ? '1 configurazione importata in ' : `${importati} configurazioni importate in `}
                <strong>{nomeDestinazione}</strong>.
              </div>
              <div className="bop-import-report__esito">
                {esiti.map(e => (
                  <div key={e.id} className="bop-import-report__esito-riga">
                    <span className="bop-text-primary">{e.nome}</span>
                    <span className={`bop-badge ${e.stato === 'IMPORTATO' ? 'bop-badge-success' : 'bop-badge-warning'}`}>
                      {e.stato === 'IMPORTATO' ? 'Importata' : 'Saltata'}
                    </span>
                    {e.motivo && <span className="bop-text-muted">{e.motivo}</span>}
                    {e.nota && <span className="bop-text-muted">{e.nota}</span>}
                  </div>
                ))}
              </div>
              {importati > 0 && notaEsito && <div className="bop-import-report__nota">{notaEsito}</div>}
            </>
          ) : (
            <>
              <div className="bop-import-report__intro">{intro}</div>
              <div className="bop-import-report__toolbar">
                <select className="bop-select" value={sorgenteId} onChange={e => cambiaSorgente(e.target.value)}>
                  <option value="">— Società di origine —</option>
                  {disponibili.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome} ({s.conteggio})</option>
                  ))}
                </select>
                <input
                  className="bop-input"
                  placeholder="Cerca..."
                  value={filtro}
                  onChange={e => setFiltro(e.target.value)}
                  disabled={!sorgenteId}
                />
              </div>

              {disponibili.length === 0 && sorgenti.length > 0 && (
                <p className="bop-text-muted">Nessun'altra società con configurazioni da importare.</p>
              )}

              {sorgenteId && (
                <div className="bop-import-report__lista">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th className="bop-table__th--select">
                          <input type="checkbox" checked={tuttiSelezionati} onChange={toggleTutti} disabled={filtrati.length === 0} />
                        </th>
                        {colonne.map(c => <th key={c.titolo}>{c.titolo}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {caricando ? (
                        <tr><td colSpan={colonne.length + 1}><div className="bop-empty-state">Caricamento...</div></td></tr>
                      ) : filtrati.length === 0 ? (
                        <tr><td colSpan={colonne.length + 1}><div className="bop-empty-state">Nessuna configurazione trovata</div></td></tr>
                      ) : (
                        filtrati.map(el => (
                          <tr key={el.id} className="bop-import-report__riga" onClick={() => toggle(el.id)}>
                            <td className="bop-table__td--select">
                              <input
                                type="checkbox"
                                checked={selezionati.includes(el.id)}
                                onChange={() => toggle(el.id)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            {colonne.map(c => <td key={c.titolo}>{c.valore(el)}</td>)}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bop-modal__footer">
          {esiti ? (
            <button className="bop-btn-primary" onClick={onChiudi}>Chiudi</button>
          ) : (
            <>
              <span className="bop-text-muted bop-import-report__conteggio">
                {selezionati.length} selezionat{selezionati.length === 1 ? 'a' : 'e'}
              </span>
              <button className="bop-btn-secondary" onClick={onChiudi}>Annulla</button>
              <button className="bop-btn-primary" onClick={esegui} disabled={importando || selezionati.length === 0}>
                {importando ? 'Importazione...' : `Importa ${selezionati.length || ''}`.trim()}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
